import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { useMe } from '@/auth/useMe';
import { useAuth } from '@/auth/AuthProvider';
import { authMode, entraConfigured, missingEntraConfig } from '@/auth/config';
import { consumeRestoreTarget } from '@/auth/redirect-state';
import { AccessNotProvisioned } from '@/components/AccessNotProvisioned';
import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Logout } from '@/pages/Logout';
import { loadChunk } from '@/lib/chunk-recovery';
import {
  ExportPolicyGate,
  needsExportPolicyAcknowledgment,
} from '@/features/export/ExportPolicyGate';
import { OpportunityList } from '@/pages/opportunities/OpportunityList';
import { OpportunityDetail, OpportunityTabRedirect } from '@/pages/opportunities/OpportunityDetail';
import { NewOpportunity } from '@/pages/opportunities/NewOpportunity';
import { AccountsList } from '@/pages/accounts/AccountsList';
import { AccountDetail } from '@/pages/accounts/AccountDetail';
import { ProgramDetail } from '@/pages/accounts/ProgramDetail';
import { RepFirmDetail } from '@/pages/accounts/RepFirmDetail';
import { ContactRedirect } from '@/pages/ContactRedirect';
import { MeetingsPage } from '@/pages/meetings/MeetingsPage';
import { ActionsPage } from '@/pages/actions/ActionsPage';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { QuickLog } from '@/pages/QuickLog';
import { StrategyList } from '@/pages/strategy/StrategyList';
import { StrategyPage } from '@/pages/strategy/StrategyPage';
import { ToastProvider } from '@/lib/toast';

/**
 * The two chart-heavy routes are the only lazy ones, and Recharts is why.
 *
 * It is ~940 kB raw / ~250 kB gzip — more than the rest of the application put
 * together — and it is used by nothing outside these two. Phase 4's principle
 * is that data-entry speed beats visual polish, and a rep who lives in the
 * list view, or who lands on an opportunity straight from a digest deep link
 * (§8e), should not pay for a charting library to get there.
 *
 * **They go through `loadChunk`, not a bare `import()`.** §3d: the SPA is
 * bundled into the Lambda package, so the previous build's hashed chunks cease
 * to exist the instant the function updates — and assets are served through
 * the API Gateway throttle, so a 429 is indistinguishable from a vanished
 * chunk unless the status is checked. `loadChunk` retries the transient case
 * and reloads only on a genuine 404; `ChunkErrorBoundary` in `main.tsx`
 * catches whatever survives that. Phase 1 built both for this moment — this is
 * the first code to have a dynamic import for them to guard.
 */
const Dashboard = lazy(() =>
  loadChunk(() => import('@/pages/Dashboard'), { loadedBuildId: __BUILD_ID__ }).then((module) => ({
    default: module.Dashboard,
  })),
);

const AnalyticsLayout = lazy(() =>
  loadChunk(() => import('@/pages/analytics/AnalyticsLayout'), {
    loadedBuildId: __BUILD_ID__,
  }).then((module) => ({ default: module.AnalyticsLayout })),
);

/**
 * `ToastProvider` wraps the router rather than sitting in `main.tsx`, because
 * every mutation in this phase reports through it — §8's pattern warning and
 * §6.10's concurrency notice both arrive as toasts — and a page mounted
 * outside it would throw on its first write. Keeping it here means any tree
 * that renders `<App />`, including a test, gets it.
 */
export function App(): React.ReactElement {
  return (
    <ToastProvider>
      <Routes>
        {/* Outside the shell: reachable without a session. */}
        <Route path="/logout" element={<Logout />} />
        <Route path="*" element={<AuthenticatedApp />} />
      </Routes>
    </ToastProvider>
  );
}

function AuthenticatedApp(): React.ReactElement {
  const navigate = useNavigate();
  const { data: me, error, isLoading, isFetching, refetch } = useMe();
  const { signIn } = useAuth();

  /**
   * Restore the path the user asked for before being bounced through Entra
   * (§8e). Runs once, after the router exists — which is why it is here rather
   * than in `main.tsx` where the redirect is actually handled.
   */
  useEffect(() => {
    const target = consumeRestoreTarget();
    if (target !== null && target !== '/') navigate(target, { replace: true });
  }, [navigate]);

  if (isLoading) {
    return <Centered>Loading…</Centered>;
  }

  if (error instanceof ApiError) {
    if (error.code === 'NO_ROLE_ASSIGNED') return <AccessNotProvisioned />;

    if (error.code === 'ACCOUNT_DEACTIVATED') {
      return (
        <Centered>
          <h1 className="text-lg font-semibold">This account has been deactivated</h1>
          <p className="text-sm text-muted-foreground">
            Contact your administrator if you believe this is a mistake.
          </p>
        </Centered>
      );
    }

    if (error.status === 401) return <SignIn onSignIn={signIn} />;

    /**
     * Everything else. **Only a 401 means unauthenticated** — a 5xx means the
     * backend is broken, and the two must not share an outcome.
     *
     * Without this branch a 500 matched none of the cases above and fell
     * through to the `!me` line below, so every backend fault rendered as
     * "you are signed out". Prod's first sign-in looped for an hour on exactly
     * that: migrations had not run, `/api/me` was returning
     * `500 A database error occurred.`, and the symptom that reached the field
     * was "it keeps logging me out" — which sent an hour into Entra's redirect
     * URIs, consent and assignment, all of which were fine. See `docs/bugs-fixed.md`
     * and PROJECT-STATE.md §5.
     *
     * A 403 from the origin-secret check (§3a) lands here too, and belongs
     * here: that is a broken deployment, not a missing session.
     */
    return (
      <BackendUnavailable error={error} onRetry={() => void refetch()} retrying={isFetching} />
    );
  }

  if (!me) return <SignIn onSignIn={signIn} />;

  /**
   * §8 item 3. Phase 3's middleware refuses every route but health, `/api/me`
   * and the acknowledgment endpoint, so on day one **every** user — the Admin
   * included — sees this and nothing else until they accept. It fires on a
   * version change too, not only for new users.
   */
  if (needsExportPolicyAcknowledgment(me)) {
    return <ExportPolicyGate me={me} />;
  }

  return (
    <AppShell>
      {/*
        The fallback is a line of text, not a skeleton: the chunk arrives in a
        few hundred milliseconds on any real connection, and a skeleton that
        flashes for that long reads worse than a quiet label.
      */}
      <Suspense fallback={<p className="p-2 text-sm text-muted-foreground">Loading…</p>}>
        <Routes>
          <Route path="/" element={<Dashboard />} />

          <Route path="/opportunities" element={<OpportunityList />} />
          <Route path="/opportunities/new" element={<NewOpportunity />} />
          <Route path="/opportunities/:id" element={<OpportunityDetail />} />
          {/* The §8e digest links at `/opportunities/<id>/actions`. */}
          <Route path="/opportunities/:id/:tab" element={<OpportunityTabRedirect />} />

          <Route path="/accounts" element={<AccountsList />} />
          <Route path="/accounts/:id" element={<AccountDetail />} />
          <Route path="/programs/:id" element={<ProgramDetail />} />
          <Route path="/rep-firms/:id" element={<RepFirmDetail />} />
          <Route path="/contacts/:id" element={<ContactRedirect />} />

          <Route path="/meetings" element={<MeetingsPage />} />
          <Route path="/actions" element={<ActionsPage />} />
          <Route path="/log" element={<QuickLog />} />

          <Route path="/analytics/*" element={<AnalyticsLayout />} />

          <Route path="/strategy" element={<StrategyList />} />
          <Route path="/strategy/:accountId" element={<StrategyPage />} />

          <Route path="/admin/*" element={<AdminLayout />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

/**
 * What a backend fault looks like to a field user.
 *
 * The `requestId` is the whole point of showing this screen rather than a
 * generic apology: `docs/bugs.md` calls it "the single highest-leverage
 * field", because it finds the exact failing request in the Lambda logs
 * instead of guessing. Field users have neither Entra sign-in logs nor
 * devtools, so if it is not on this screen it does not reach Alex — and it is
 * rendered selectable, in a monospace face, so it survives being retyped into
 * a message.
 *
 * Retry re-runs the `/api/me` query in place. It does NOT reload the document:
 * a reload re-enters the MSAL redirect dance and puts the user back at the
 * behaviour this screen exists to stop.
 */
function BackendUnavailable({
  error,
  onRetry,
  retrying,
}: {
  error: ApiError;
  onRetry: () => void;
  retrying: boolean;
}): React.ReactElement {
  return (
    <Centered>
      <h1 className="text-lg font-semibold">Something went wrong at our end</h1>

      <p className="text-sm text-muted-foreground">
        You are signed in — this is not a sign-in problem. The server could not answer, so the
        application cannot start.
      </p>

      <p className="text-sm text-muted-foreground">{error.message}</p>

      {error.requestId !== undefined && (
        <p className="text-xs text-muted-foreground">
          Quote this reference when you report it:{' '}
          <code className="select-all font-mono text-foreground">{error.requestId}</code>
        </p>
      )}

      <div className="flex justify-center gap-2 pt-1">
        <Button size="sm" onClick={onRetry} disabled={retrying}>
          {retrying ? 'Retrying…' : 'Try again'}
        </Button>
      </div>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-md space-y-3 text-center">{children}</div>
    </div>
  );
}

function SignIn({ onSignIn }: { onSignIn: () => void }): React.ReactElement {
  /**
   * A build with neither Entra nor an explicit bypass is broken, and says so.
   *
   * It used to fall back to the dev-login screen, which is how a deployed SPA
   * compiled without its `VITE_*` build args came to advertise a bypass the API
   * rejects. §0's rule for placeholders is the same rule: a missing value must
   * fail loudly rather than resolve into something that looks like it works.
   */
  if (authMode === 'misconfigured') {
    return (
      <Centered>
        <h1 className="text-lg font-semibold">This build is not configured</h1>
        <p className="text-sm text-muted-foreground">
          Sign-in cannot start: {missingEntraConfig().join(', ')}{' '}
          {missingEntraConfig().length === 1 ? 'is' : 'are'} missing from this build.
        </p>
        <p className="text-xs text-muted-foreground">
          Vite inlines these at build time, so they are compiled in rather than read at runtime — a
          redeploy is required, not a configuration change. The values live in{' '}
          <code className="rounded bg-muted px-1">infra/config/</code> and reach the image as Docker
          build args.
        </p>
      </Centered>
    );
  }

  // `__DEV_LOGIN__` first: a literal `false` lets Rollup drop this whole branch
  // and the strings inside it from a deployed bundle.
  if (__DEV_LOGIN__ && authMode === 'dev') {
    return (
      <Centered>
        <h1 className="text-lg font-semibold">Development sign-in</h1>
        {entraConfigured ? null : (
          <p className="text-sm text-muted-foreground">
            This build sets <code className="rounded bg-muted px-1">VITE_DEV_LOGIN=1</code>, so it
            uses the <code className="rounded bg-muted px-1">x-dev-user</code> bypass rather than
            Entra. The bypass compiles out of any build that does not ask for it.
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          No seeded user matched. Run <code className="rounded bg-muted px-1">pnpm db:seed</code>{' '}
          and pick a user from the switcher in the top bar.
        </p>
      </Centered>
    );
  }

  return (
    <Centered>
      <h1 className="text-lg font-semibold">Menlo Funnel</h1>
      <p className="text-sm text-muted-foreground">Sign in with your Menlo account to continue.</p>
      <Button onClick={onSignIn}>Sign in</Button>
    </Centered>
  );
}
